import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SITE_NAME,
  SITE_ORIGIN,
  FAVICON_URL,
  homeSeo,
  articleSeoMap,
  type ArticleSeoData,
} from './seo';

function injectJsonLd(id: string, data: Record<string, unknown>) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function websiteJsonLd() {
  const url = SITE_ORIGIN ? `${SITE_ORIGIN}/` : '/';

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url,
    description: homeSeo.description,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url,
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
  };
}

function articleJsonLd(article: ArticleSeoData) {
  const url = SITE_ORIGIN ? `${SITE_ORIGIN}${article.path}` : article.path;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image ? [article.image] : undefined,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: FAVICON_URL,
      },
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };
}

export function RouteJsonLd() {
  const { pathname } = useLocation();

  useEffect(() => {
    injectJsonLd('jsonld-website', websiteJsonLd());

    const articleMatch = pathname.match(/^\/article\/([^/]+)$/);
    if (articleMatch && articleSeoMap[articleMatch[1]]) {
      injectJsonLd('jsonld-article', articleJsonLd(articleSeoMap[articleMatch[1]]));
    } else {
      const existing = document.getElementById('jsonld-article');
      if (existing) existing.remove();
    }
  }, [pathname]);

  return null;
}